import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { ProductVisual } from '../components/ProductVisual';
import { ProductCard } from '../components/ProductCard';
import { ReadingCTA } from '../components/ReadingCTA';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { Button } from '../components/Button';
import { Dial } from '../components/Dial';
import { useCart } from '../contexts/CartContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { getProductPrice, products } from '../data/products';
import { images } from '../data/images';
import { formatPrice } from '../utils/currency';
type View = 'product' | 'detail' | 'lifestyle';
export function ProductDetail() {
  const {
    slug
  } = useParams();
  const product = products.find(p => p.slug === slug);
  const {
    addItem
  } = useCart();
  const [view, setView] = useState<View>('product');
  const [option, setOption] = useState('');
  const [qty, setQty] = useState(1);
  const [optionError, setOptionError] = useState('');
  usePageMeta(product ? product.name : 'Product not found', product ? product.story : 'This piece could not be found.');
  if (!product) {
    return <section className="gutter section text-center" data-id="element-800" data-mp="3axp31gbb9dn5">
        <h1 className="t-h2" data-id="element-801" data-mp="3fu5scdu8xy1t">This piece isn't here.</h1>
        <p className="mt-4 text-graphite" data-id="element-802" data-mp="3fskw8jsaa71u">It may have moved or sold out.</p>
        <Button to="/shop" className="mt-8" data-id="element-803" data-mp="7fnd9s5w1i8p">
          Back to the Shop
        </Button>
      </section>;
  }
  const views: View[] = product.image ? ['product', 'detail', 'lifestyle'] : ['product'];
  const price = getProductPrice(product, option);
  const related = products.filter(p => p.id !== product.id && p.status === 'available' && p.categories.some(c => product.categories.includes(c))).slice(0, 4);
  function renderView(v: View, thumb = false) {
    if (v === 'lifestyle') {
      return <img src={images.ritual} alt={thumb ? '' : 'Lifestyle: hands with gold jewelry lighting a candle on stone'} className="h-full w-full object-cover" data-id="element-804" data-mp="2n7kzta21bfwo" />;
    }
    return <ProductVisual product={product!} className="h-full w-full" imgClassName={v === 'detail' ? 'scale-[1.6]' : ''} data-id="element-805" data-mp="1z3ms1sfyq5fa" />;
  }
  function handleAdd() {
    if (product!.option && !option) {
      setOptionError(`Choose a ${product!.option.label.toLowerCase()}.`);
      return;
    }
    setOptionError('');
    addItem(product!.id, option || undefined, qty);
  }
  return <>
      <nav aria-label="Breadcrumb" className="gutter pt-6 text-sm text-graphite" data-id="element-806" data-mp="1fe2yfp8g53ru">
        <ol className="flex gap-2" data-id="element-807" data-mp="1bczkj89ylht9">
          <li data-id="element-808" data-mp="1be0sh3voho6r">
            <Link to="/shop" className="hover:text-charcoal hover:underline underline-offset-4" data-id="element-809" data-mp="1bdj0wkjyny56">
              Shop
            </Link>
          </li>
          <li aria-hidden="true" data-id="element-810" data-mp="35qqops9ir9hb">/</li>
          <li aria-current="page" className="text-charcoal" data-id="element-811" data-mp="35ppcvlkoiiyj">
            {product.name}
          </li>
        </ol>
      </nav>

      <section className="gutter grid gap-[clamp(2rem,5vw,7rem)] pb-[clamp(4rem,8vw,9rem)] pt-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-id="element-812" data-mp="30sp8rg7e3qa2">
        <div className="grid gap-3 md:grid-cols-[5.5rem_minmax(0,1fr)]" data-id="element-813" data-mp="3d9zwjh2dn049">
          <div className="order-2 flex gap-3 md:order-1 md:flex-col" data-id="element-814" data-mp="3d86t0an28w0h">
            {views.map(v => <button key={v} type="button" onClick={() => setView(v)} aria-label={`Show ${v} view`} aria-pressed={view === v} className={`aspect-square w-20 overflow-hidden md:w-full ${view === v ? 'ring-1 ring-charcoal' : 'opacity-80 hover:opacity-100'}`} data-id="element-815" data-mp="3n0cqfzw305n2">
                {renderView(v, true)}
              </button>)}
          </div>
          <div className="relative order-1 aspect-square overflow-hidden md:order-2" data-id="element-816" data-mp="33dwwqhuzzdkh">
            {renderView(view)}
            <NeedFromAbby className="absolute left-3 top-3" data-id="element-817" data-mp="emlxzzjced83">product photography</NeedFromAbby>
          </div>
        </div>

        <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-818" data-mp="h4z2pczoalqo">
          <p className="t-label text-[0.66rem] text-gold-deep" data-id="element-819" data-mp="h57k9i6qqf87">{product.categories[0]}</p>
          <h1 className="t-wordmark mt-4 text-[clamp(1.5rem,1.2rem+1vw,2.5rem)] font-light leading-tight text-charcoal" data-id="element-820" data-mp="c7ffvoowt0n4">{product.name}</h1>
          <p className="mt-4 text-[1.35rem] font-light text-charcoal" data-id="element-821" data-mp="m41zka5i1lnk">
            {product.status === 'coming-soon' ? 'Coming soon' : formatPrice(price)}
          </p>
          <p className="mt-6 max-w-[52ch] text-graphite" data-id="element-822" data-mp="1020dxfjtli32">{product.story}</p>

          <div className="mt-8 flex items-start gap-4 bg-cream p-5" data-id="element-823" data-mp="v3yuuume9irl">
            <Dial numerals={false} hands={false} star={false} className="h-10 w-10 shrink-0" data-id="element-824" data-mp="19u636hiotlek" />
            <div data-id="element-825" data-mp="14utju6r0ywgq">
              <p className="t-label text-[0.62rem] text-charcoal" data-id="element-826" data-mp="14u9z6i9zptz0">Aligned with</p>
              <p className="mt-1 text-sm text-graphite" data-id="element-827" data-mp="1itt6j8uz91a9">{product.alignedWith}</p>
            </div>
          </div>

          {product.option && <fieldset className="mt-8" data-id="element-828" data-mp="1euaiw5bruc3q">
              <legend className="t-label text-[0.66rem] text-charcoal" data-id="element-829" data-mp="1xh9odjttffoz">
                {product.option.label}
                {option && <span className="ml-2 normal-case tracking-normal text-graphite" data-id="element-830" data-mp="22gcwjd53e216">· {option}</span>}
              </legend>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-id="element-831" data-mp="1ofjyiifl0otb">
                {product.option.values.map(v => <button key={v} type="button" aria-pressed={option === v} onClick={() => {
              setOption(v);
              setOptionError('');
            }} className={`h-11 border px-2 text-sm transition-colors duration-150 ${option === v ? 'border-charcoal bg-charcoal text-white' : 'border-[#8F877F] text-charcoal hover:border-charcoal'}`} data-id="element-832" data-mp="1thj1hr7etjpq">
                    {v}
                  </button>)}
              </div>
              {optionError && <p role="alert" className="mt-2 text-sm text-[#9B2C2C]" data-id="element-833" data-mp="32gq2k7wsbyeg">
                  {optionError}
                </p>}
            </fieldset>}

          {product.status === 'available' ? <div className="mt-8 flex gap-3" data-id="element-834" data-mp="2tnuvd8nrbsm1">
              <div className="flex items-center border border-[#8F877F]" data-id="element-835" data-mp="2op1s0926phzl">
                <button type="button" aria-label="Decrease quantity" onClick={() => setQty(q => Math.max(1, q - 1))} className="grid h-14 w-12 place-items-center" data-id="element-836" data-mp="3lesmi8ah755e">
                  <MinusIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4" data-id="element-837" data-mp="3hdpaxxzgnv9z" />
                </button>
                <span className="w-8 text-center" aria-live="polite" aria-label={`Quantity ${qty}`} data-id="element-838" data-mp="3cg78mqfy8eay">
                  {qty}
                </span>
                <button type="button" aria-label="Increase quantity" onClick={() => setQty(q => q + 1)} className="grid h-14 w-12 place-items-center" data-id="element-839" data-mp="20z7gpvxik1hg">
                  <PlusIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4" data-id="element-840" data-mp="1w1xknxfmcrkn" />
                </button>
              </div>
              <Button size="lg" onClick={handleAdd} className="flex-1" data-id="element-841" data-mp="1qur030c7mgcl">
                Add to Cart
              </Button>
            </div> : <Button to="/shop#watch" size="lg" className="mt-8 w-full" data-id="element-842" data-mp="1vsslvt10strr">
              Join the Waitlist
            </Button>}

          <ul className="mt-10 space-y-2 border-t border-stone pt-6 text-sm text-graphite" data-id="element-843" data-mp="24mnpuxqeoe5t">
            {product.details.map(d => <li key={d} className="flex items-start gap-3" data-id="element-844" data-mp="29k73pwj6yox8">
                <span aria-hidden="true" className="mt-[0.65em] h-px w-3 shrink-0 bg-gold" data-id="element-845" data-mp="2ekuvwlj5pbt1" />
                {d}
              </li>)}
          </ul>
          <div className="mt-6" data-id="element-846" data-mp="2jiwlzqf02be0">
            <NeedFromAbby data-id="element-847" data-mp="k6tvhw7ykgqu">final price, materials &amp; care</NeedFromAbby>
          </div>
        </div>
      </section>

      <section className="gutter pb-[clamp(4rem,8vw,9rem)]" aria-label="Pair with a reading" data-id="element-848" data-mp="p4ve6g1h2e1s">
        <ReadingCTA proofIndex={0} data-id="element-849" data-mp="2r2noi7evg86r" />
      </section>

      {related.length > 0 && <section aria-labelledby="related-title" className="gutter pb-[clamp(4rem,8vw,9rem)]" data-id="element-850" data-mp="2m4dtpxx279lo">
          <h2 id="related-title" className="t-label text-charcoal" data-id="element-851" data-mp="30sqtl5z9pclg">
            You may also like
          </h2>
          <ul className="mt-8 grid gap-x-[clamp(1rem,1.6vw,2rem)] gap-y-10 [grid-template-columns:repeat(auto-fill,minmax(min(100%,clamp(9.5rem,19vw,26rem)),1fr))]" data-id="element-852" data-mp="2vtdwztilgr5o">
            {related.map(p => <li key={p.id} data-id="element-853" data-mp="235rw4t0r40mi">
                <ProductCard product={p} data-id="element-854" data-mp="236t4uquaob12" />
              </li>)}
          </ul>
        </section>}
    </>;
}